/**
 * User-provided data (UPD) for Google: the visitor's email, normalized and
 * SHA-256 hashed IN THE BROWSER, sent as `user_data.sha256_email_address`
 * alongside the event it belongs to (newsletter_signup, generate_lead,
 * purchase). The plain address never enters the dataLayer.
 *
 * In GTM, each of those GA4 Event tags reads it through an event parameter
 * `user_data` bound to a User-Provided Data variable (Data Layer Variable
 * `user_data`), and the Google tag has "Allow user-provided data capabilities"
 * on — see docs/google-user-provided-data.md. Google's key names and
 * normalization: support.google.com/analytics/answer/14179230 (GA4, Tag
 * Manager code snippet) and Google Ads' enhanced-conversions normalization
 * (lowercase, trim, no spaces, no dots before @ for gmail.com/googlemail.com),
 * which is what Google applies when it hashes an unhashed `email` itself.
 *
 * Sent whenever measurement is allowed for this visitor (the regional default
 * outside the opt-in list, or an Accept), and never when it is off: opt-in
 * region before Accept, GPC, Decline (measurement-consent convention C13).
 * The one decision is `privacyConsent.allowed`, read through isTrackingAllowed().
 */
import { isTrackingAllowed } from "@/lib/consent/privacy";

export interface GoogleUserData {
  sha256_email_address: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Google's email normalization, before hashing. Returns null for anything that isn't an email. */
export function normalizeEmailForGoogle(raw: string): string | null {
  const email = raw.trim().toLowerCase().replace(/\s+/g, "");
  if (!EMAIL_RE.test(email)) return null;
  const [local, domain] = email.split("@");
  if (domain === "gmail.com" || domain === "googlemail.com") {
    return `${local.replace(/\./g, "")}@${domain}`;
  }
  return email;
}

/** Lowercase hex SHA-256 of a UTF-8 string. */
export async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * The `user_data` object for an event, or undefined when there's no usable
 * email, no consent, or no Web Crypto (non-secure context). Never throws:
 * tracking must not break a form submission or a thank-you page.
 */
export async function buildGoogleUserData(
  email: string | null | undefined
): Promise<GoogleUserData | undefined> {
  try {
    if (!email || !isTrackingAllowed()) return undefined;
    if (typeof crypto === "undefined" || !crypto.subtle) return undefined;
    const normalized = normalizeEmailForGoogle(email);
    if (!normalized) return undefined;
    const hash = await sha256Hex(normalized);
    // Re-check: the visitor may have declined while the hash was computed.
    return isTrackingAllowed() ? { sha256_email_address: hash } : undefined;
  } catch {
    return undefined;
  }
}
